import * as React from 'react';
import Axios from 'src/services/axios';
import { isNil, merge, concat, mapValues, omitBy, flatten } from 'lodash';
import styles from './DividedColumnDetails.styles';
import {
  DividedColumnDetailsProps,
  DividedColumnDetailsState,
  DividedColumnDetailsSection,
  DividedColumnDetailsSectionColumn,
  ValueSelection,
  ValuePreviousSelection,
  MixedPromiseResponse,
} from './DividedColumnDetails.types';
import { processApiParams, getUrl, getViewProm } from '../StyleEdit.utils';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import ServiceContainer from 'src/ServiceContainer';
import store from 'src/store';
import {
  getAttributeBlacklist,
  getCurrentDepartment,
} from 'src/components/ConfigurableGrid/ConfigurableGrid.selectors';
import { blacklistByDataIndex } from 'src/utils/Component/AttributeBlacklist.utils';
import type { BasicViewItem } from 'configuration-schemas/viewdefns/general';
import Overlay from 'src/common-ui/components/Overlay/Overlay';
import * as globalMath from 'mathjs';
import { maybeReturnNestData } from 'src/utils/Http/NestedDatas';
import LocalPivotServiceContext from 'src/components/StylePreview/PivotServiceContext';
import { toast } from 'react-toastify';
import { errorToLoggingPayload, logError } from 'src/services/loggingService';
import { createId } from '@paralleldrive/cuid2';
import { AppType } from 'configuration-schemas/general';
import { submitGranular } from '../StyleEditSection/StyleEditSection.utils';
import { GranularEditPayloadItem } from 'src/dao/pivotClient';
import { Suggestion } from 'src/common-ui/components/Inputs/InputSuggest/InputSuggest';
import { getValidValues } from '../StyleEditSection/StyleEditSection.client';
import { AsyncCellState } from 'src/components/ConfigurableGrid/ConfigurableGrid.types';
import { updateNodeAsyncState } from 'src/components/ConfigurableGrid/utils/ConfigurableGrid.utils';
import {
  checkKeyAsyncState,
  columnConfigToEditableField,
  createSavePayload,
  getDisplayValueForDividedColumnDetails,
  handleDividedColumnDetailsDropdownChange,
} from './DividedColumnDetails.utils';

export default class ColumnDetails extends React.Component<DividedColumnDetailsProps, DividedColumnDetailsState> {
  state: DividedColumnDetailsState;
  math = globalMath.create(globalMath.all) as globalMath.MathJsStatic;
  static contextType = LocalPivotServiceContext;
  context!: React.ContextType<typeof LocalPivotServiceContext>;
  componentId: string = createId();

  constructor(props: DividedColumnDetailsProps) {
    super(props);

    this.state = {
      isLoading: false,
      dependentProps: {},
      dependentCalcs: {},
      openDropdownKey: null,
      dropdownOptions: {},
    };
  }

  componentDidMount() {
    this.getData();
  }

  componentDidUpdate(prevProps: DividedColumnDetailsProps) {
    if (this.props.styleId !== prevProps.styleId) {
      this.getData(true);
      // TODO: Do we still need this?
    } else if (this.props.currentRefreshCount > prevProps.currentRefreshCount) {
      const isCurrent = this.props.lastEditedSection === this.componentId;
      if (isCurrent) {
        // At the direction of management, we only want to reload 'dependent' dropdowns each time
        // dropdowns are on their own.  If a granular happens to modify these (such as from a trigger change)
        // then dropdowns will also be wrong.
        // This decision was made as it was believed extra data fetches would slow the view down
        this.getColumnValidValues(['dependentDropdown']);
      } else {
        this.getData(true);
      }
    }
  }

  //#region Data Fetching
  redecorateData = async () => {
    const { dataApi } = this.props;
    if (!dataApi.isListData || isNil(this.state.data)) {
      this.setState({ isLoading: false });
      return;
    }
    // hardcoding this to use product: id for redecoration
    const productId = this.state.data.id;
    const newData = await ServiceContainer.pivotService.redecorate({
      coordinates: [{ product: productId }],
      defnId: dataApi.defnId,
      nestData: false,
      aggBy: dataApi.params.aggBy?.split(',') ?? [],
    });
    this.setState({
      data: newData[0],
    });
  };

  getQueue() {
    const { styleId, dataApi, configApi } = this.props;
    const listDataCall = this.context.listData;
    let dataProm: Promise<MixedPromiseResponse>;
    if (dataApi.isListData) {
      dataProm = listDataCall(dataApi.defnId, AppType.Assortment, dataApi.params).then((resp) => {
        return {
          data: {
            data: resp.flat[0],
          },
        };
      });
    } else {
      const dataUrl = getUrl(dataApi);
      dataProm = Axios.get(dataUrl);
    }

    let requests = [getViewProm(configApi), dataProm];
    if (!isNil(styleId)) {
      const encodedValue = encodeURIComponent(styleId);
      requests = concat(
        requests,
        Axios.get(`/api/member/ancestors/mapping?memberId=${encodedValue}&dimension=product`),
      );
    }

    return requests;
  }

  getData(override?: boolean) {
    // trigger the loader display
    if (this.state.isLoading && !override) return;
    this.setState(
      {
        isLoading: true,
      },
      () => {
        const queue = this.getQueue();

        const promise = Promise.all(queue).then((resp) => {
          // [0] -> viewDefn
          // [1] -> "row" information
          // [2] -> hierarchy mapping data
          const sectionData = maybeReturnNestData(resp[0]) as any;

          // FIXME drill this down as props or move the whole thing up and out
          const reduxState = store.getState();
          const blacklist = getAttributeBlacklist(reduxState);
          const currentDepartment = getCurrentDepartment(reduxState);
          if (blacklist && currentDepartment && Array.isArray(sectionData.sections)) {
            sectionData.sections = sectionData.sections.map((section: any) =>
              section?.columns
                ? {
                    ...section,
                    columns: blacklistByDataIndex(section.columns as BasicViewItem[], blacklist, currentDepartment),
                  }
                : section,
            );
          }

          const styleData = resp[1].data.data;
          let styleMemberData = null;

          if (queue.length > 2) {
            // merge ancestor info into styleData
            const ancestorData = resp[2].data.data;
            const memberData = {} as BasicPivotItem;
            Object.keys(ancestorData).forEach((level: string) => {
              memberData[`member:${level}:id`] = ancestorData[level]['id'];
              memberData[`member:${level}:name`] = ancestorData[level]['name'];
            });

            styleMemberData = merge(memberData, styleData);
          }

          const sections: DividedColumnDetailsSection[] = sectionData.sections;
          const dependentProps = {} as (typeof this.state)['dependentProps'];

          if (!isNil(sectionData.dependentProps)) {
            const keys = Object.keys(sectionData.dependentProps);
            keys.forEach((prop: string) => {
              const dependents = sectionData.dependentProps[prop];
              dependentProps[prop] = dependents;
            });
          }

          const dependentCalcs = {} as (typeof this.state)['dependentCalcs'];
          if (sections) {
            sections.forEach((section) =>
              section.columns.forEach((column) => {
                if (column.dependentCalc) {
                  dependentCalcs[column.dataIndex] = column.dependentCalc;
                }
              }),
            );
          }
          const loadedData = queue.length > 2 ? styleMemberData : styleData;
          this.setState(
            {
              isLoading: false,
              sections,
              data: loadedData,
              dependentProps,
              dependentCalcs,
            },
            () => {
              // pre-fetch dropdownOptions to check validity
              this.getColumnValidValues();
            },
          );
        });

        promise.catch((err: any) => {
          toast.error('An error occurred fetching your style edit data');
          ServiceContainer.loggingService.error(`An error occurred: ${errorToLoggingPayload(err)}`, err.stack);
        });
      },
    );
  }

  getColumnValidValues(inputTypes: string[] = ['dropdown', 'dependentDropdown']) {
    const loadedData = this.state.data;
    if (!loadedData) {
      return;
    }

    this.state.sections?.forEach((section) => {
      section.columns.forEach((column) => {
        if (inputTypes.includes(column.inputType ?? '') && column.dataApi) {
          const api = processApiParams(column.dataApi, loadedData);
          const url = getUrl(api);
          const allowOpt = column.allowEmptyOption ?? true;
          const concat = column.concatOptionValues ?? false;

          getValidValues(url, allowOpt, concat)
            .then((validValues: Suggestion[]) => {
              this.setState((state) => ({
                dropdownOptions: {
                  ...state.dropdownOptions,
                  [column.dataIndex]: validValues,
                },
              }));
            })
            .catch((err) => {
              logError(`Error loading options for ${column.dataIndex}`, err);
            });
        }
      });
    });
  }
  //#endregion

  applyAsync(dataIndexes: string[], state: AsyncCellState) {
    if (this.state.data != null) {
      const newState = { ...this.state.data };
      updateNodeAsyncState(newState, dataIndexes, state);
      this.setState({ data: newState });
    }
  }

  getKeyAsyncState(dataIndex: string) {
    return checkKeyAsyncState(dataIndex, this.state.data);
  }

  createDividedColumnDetailSection(viewDefn: DividedColumnDetailsSection, sectionKey: string) {
    const halfLength = Math.ceil(viewDefn.columns.length / 2);
    const leftSide = this.renderColumns(viewDefn.columns.slice(0, halfLength));
    const rightSide = this.renderColumns(viewDefn.columns.slice(halfLength));

    return (
      <div key={sectionKey} className={styles.container}>
        {viewDefn.text && (
          <div className={styles.titleColumn} data-qa="section-title">
            <span>{viewDefn.text}</span>
          </div>
        )}
        <div className={styles.dataColumnWrapper} data-qa="section-data-column-wrapper">
          {leftSide}
          {rightSide}
        </div>
        <div className={styles.divider} />
      </div>
    );
  }

  renderColumns(columns: DividedColumnDetailsSectionColumn[]) {
    const { data } = this.state;
    const values: DividedColumnDetailsSectionColumn[] = [];

    columns.forEach((column) => {
      values.push({
        ...column,
        text: !isNil(data) ? data[column.dataIndex] : 'N/A',
      });
    });

    return (
      <div className={styles.dataColumn} data-qa="section-data-column">
        <div className={styles.textColumn}>{columns.map((column) => this.createLabelComponent(column))}</div>
        <div className={styles.valueColumn}>
          {values.map((item, index) => {
            let loadingClass: string = '';
            switch (this.getKeyAsyncState(item.dataIndex)) {
              case AsyncCellState.Processing:
                loadingClass = 'loading';
                break;
              case AsyncCellState.Redecorating:
                loadingClass = 'redecorating';
                break;
            }
            const key = `${item.dataIndex}-${index}`;
            return (
              <div className={loadingClass} key={key}>
                {this.createValueComponent(item, key)}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  createLabelComponent = (item: DividedColumnDetailsSectionColumn) => {
    const text = item.text;
    if (item.hidden) {
      return null;
    }
    return (
      <div
        className={styles.dataItem}
        style={{ height: item.inputParams?.height }}
        key={text}
        data-qa="column-value-label"
      >
        {text}
      </div>
    );
  };

  /**
   * Get the display label(s) from the prefetched options
   */
  private getDisplayValue(
    dataIndex: string,
    rawValue: string | string[],
    multiSelect: boolean | undefined,
  ): string | string[] {
    return getDisplayValueForDividedColumnDetails(dataIndex, rawValue, multiSelect, this.state.dropdownOptions);
  }

  showEditor = (rendererKey: string) => this.setState({ openDropdownKey: rendererKey });
  showRenderer = () => this.setState({ openDropdownKey: null });

  createValueComponent = (item: DividedColumnDetailsSectionColumn, fieldId: string) => {
    const { styleId } = this.props;
    const { data, dropdownOptions, dependentCalcs } = this.state;

    return columnConfigToEditableField({
      item,
      fieldId,
      styleId,
      data,
      dropdownOptions,
      dependentCalcs,
      mathInstance: this.math,
      updateCoordinateMap: this.props.updateCoordinateMap,
      onCloseCallback: this.showRenderer,
      onClickCallback: this.showEditor,
      openDropdownKey: this.state.openDropdownKey,
      handleDropdownChange: this.handleDropdownChange,
    });
  };

  handleDropdownChange = (selection: ValueSelection, item: DividedColumnDetailsSectionColumn) => {
    // if the selection is undefined/null, don't go through this (dependent dropdowns without options or
    if (isNil(selection)) return;
    const { styleId } = this.props;
    const { data, dependentCalcs } = this.state;
    const { dataIndex } = item;

    const maybeUpdatedData = handleDividedColumnDetailsDropdownChange({
      selection,
      item,
      styleId: this.props.styleId,
      data: data,
      dependentCalcs: dependentCalcs,
      mathInstance: this.math,
      updateCoordinateMap: this.props.updateCoordinateMap,
    });

    if (!maybeUpdatedData) return;

    const { updatedData, updatedValue, payloads } = maybeUpdatedData;

    this.setState(
      {
        data: updatedData,
      },
      () => {
        const dropdownPayload = createSavePayload(
          styleId,
          dataIndex,
          updatedValue,
          this.props.updateCoordinateMap,
          this.state.data,
        );
        dropdownPayload != null && payloads.push(dropdownPayload);
        this.saveDataFromPayloads(payloads);
      },
    );
  };

  //#region Submit update Data
  getPayloadsUpdateKeys(payloads: GranularEditPayloadItem[]) {
    return flatten(
      payloads.map((p) => {
        return Object.keys(p).filter((p) => p != 'coordinates');
      }),
    );
  }

  saveDataFromPayloads(payloads: GranularEditPayloadItem[]) {
    const updateKeys = this.getPayloadsUpdateKeys(payloads);
    this.applyAsync(updateKeys, AsyncCellState.Processing);
    submitGranular(payloads)
      .then(async () => {
        this.context.clearPivotCache();
        this.applyAsync(updateKeys, AsyncCellState.Redecorating);
        await this.redecorateData();
        // re-introduce refreshSections to update the UI
        this.props.refreshSections(this.componentId);
      })
      .catch((e) => {
        toast.error('An error occured updating your style');
        ServiceContainer.loggingService.error('Error in granular update', e.stack);
      })
      .finally(() => {
        this.applyAsync(updateKeys, AsyncCellState.Idle);
      });
  }

  saveData(styleId: string, dataIndex: string, value: string | number, _prevSelection?: ValuePreviousSelection) {
    const { updateCoordinateMap } = this.props;
    const row = this.state.data;
    if (row == null) {
      return;
    }

    // build coords, either from the passed‐in map or default to product:id
    const coords = updateCoordinateMap
      ? omitBy(
          mapValues(updateCoordinateMap, (field) => row[field]),
          isNil,
        )
      : { product: styleId }; // fallback to what /product endpoint used
    // This fun mess removes that silly wrapped colon stuff (eg: attribute:<x>:id)
    // This is the more concise version: .replace(/(?:^.*?:)?([^:]*)(?::.*)?/, '$1')
    const formattedDataIndex = dataIndex.replace(/(member|attribute):/, '').replace(/:(id|name|description)/, '');
    // build payload item
    const payload: GranularEditPayloadItem = {
      coordinates: coords,
      [formattedDataIndex]: value,
    };

    submitGranular([payload])
      .then(async () => {
        this.context.clearPivotCache();
        await this.redecorateData();
        // re-introduce refreshSections to update the UI
        this.props.refreshSections(this.componentId);
      })
      .catch((e) => {
        toast.error('An error occured updating your style');
        ServiceContainer.loggingService.error('Error in granular update', e.stack);
      });
  }

  //#endregion

  render() {
    if (!this.props.expanded) {
      // in the mounted but in un-expanded state, renderer nothing to save resources
      return <React.Fragment></React.Fragment>;
    }
    if (this.state.isLoading) {
      return (
        <Overlay type="loading" visible={true} fitParent={true} qaKey={'StyleEditSectionDividedColumnDetailsOverlay'} />
      );
    }

    const { sections } = this.state;
    if (isNil(sections)) {
      return null;
    }

    const columns = sections.map((section, index) => {
      return this.createDividedColumnDetailSection(section, `${section.text}-${index}`);
    });
    return (
      <div className={styles.outerContainer} data-qa="column-details-container">
        {columns}
      </div>
    );
  }
}
